import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Switch,
  Button,
  Typography,
  Row,
  Col,
  Divider,
  message,
  Space,
  Modal,
  Form,
  Input,
  Alert,
  Spin,
} from 'antd';
import { SettingOutlined, BellOutlined, LockOutlined, SaveOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { Title, Text } = Typography;

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [twoFactorModalVisible, setTwoFactorModalVisible] = useState(false);
  const [disable2FAModalVisible, setDisable2FAModalVisible] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const autoSaveTimer = useRef(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { isLoading } = useQuery('provider-settings', async () => {
    const response = await api.get('/provider/profile');
    const data = response.data.data || response.data;
    setSettings({
      email_notifications: data.email_notifications ?? true,
      push_notifications: data.push_notifications ?? true,
      sms_notifications: data.sms_notifications ?? false,
      order_notifications: data.order_notifications ?? true,
      promotional_emails: data.promotional_emails ?? false,
      newsletter: data.newsletter ?? false,
      is_online: data.is_online ?? false,
      profile_visibility: data.profile_visibility ?? true,
      show_contact_info: data.show_contact_info ?? true,
      two_factor_enabled: data.two_factor_enabled ?? false,
    });
    return data;
  });

  // Auto-save settings after 1.5s debounce
  useEffect(() => {
    if (isLoading) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      updateSettingsMutation.mutate(settings);
    }, 1500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const updateSettingsMutation = useMutation(
    (settingsData) => {
      const formData = new FormData();
      Object.keys(settingsData).forEach((key) => {
        formData.append(key, settingsData[key]);
      });
      return api
        .post('/provider/profile-update', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => res.data);
    },
    {
      onSuccess: (response) => {
        queryClient.setQueryData('provider-settings', response.data.data || response.data);
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update settings');
      },
    },
  );

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateSettingsMutation.mutateAsync(settings);
      message.success('Settings saved');
    } catch (error) {
      console.error('Settings update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const changePasswordMutation = useMutation(
    (passwordData) => {
      return api.post('/provider/change-password', passwordData).then((res) => res.data);
    },
    {
      onSuccess: (response) => {
        if (response.success) {
          message.success('Password changed successfully');
          setPasswordModalVisible(false);
          form.resetFields();
        } else {
          message.error(response.message || 'Failed to change password');
        }
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to change password');
      },
    },
  );

  const handlePasswordChange = async (values) => {
    try {
      await changePasswordMutation.mutateAsync(values);
    } catch (error) {
      console.error('Password change error:', error);
    }
  };

  const handleToggle2FA = async (enable) => {
    if (enable) {
      setTwoFactorModalVisible(true);
      setTwoFactorLoading(true);
      try {
        const response = await api.get('/provider/generate-2fa-secret').then((res) => res.data);

        if (response.success) {
          setTwoFactorData(response);
          message.info('Scan the QR code with your authenticator app');
        } else {
          message.error(response.message || 'Failed to generate 2FA secret');
        }
      } catch (error) {
        message.error('Failed to generate 2FA secret');
      } finally {
        setTwoFactorLoading(false);
      }
    } else {
      setDisable2FAModalVisible(true);
    }
  };

  const handleDisable2FA = async (values) => {
    setTwoFactorLoading(true);
    try {
      const response = await api
        .post('/provider/disable-2fa', { password: values.password })
        .then((res) => res.data);

      if (response.success) {
        message.success('2FA disabled successfully');
        setDisable2FAModalVisible(false);
        setSettings((prev) => ({ ...prev, two_factor_enabled: false }));
        queryClient.invalidateQueries('provider-settings');
      } else {
        message.error(response.message || 'Failed to disable 2FA');
      }
    } catch (error) {
      message.error('Failed to disable 2FA');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleSetup2FA = async (values) => {
    setTwoFactorLoading(true);
    try {
      const response = await api
        .post('/provider/setup-2fa', {
          code: values.code,
          password: values.password,
        })
        .then((res) => res.data);

      if (response.success) {
        message.success('2FA enabled successfully');
        setTwoFactorModalVisible(false);
        setTwoFactorData(null);
        form.resetFields();
        setSettings((prev) => ({ ...prev, two_factor_enabled: true }));
        queryClient.invalidateQueries('provider-settings');
      } else {
        message.error(response.message || 'Failed to enable 2FA');
      }
    } catch (error) {
      message.error('Failed to enable 2FA');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  if (isLoading) {
    return <div>Loading settings...</div>;
  }

  return (
    <div>
      <Title level={2} className="page-title">
        Settings
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <>
                <SettingOutlined /> Notification Settings
              </>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Email Notifications</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Receive email updates about orders and customers
                  </Text>
                </div>
                <Switch
                  checked={settings.email_notifications}
                  onChange={(checked) => handleSettingChange('email_notifications', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Push Notifications</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Receive push notifications on your devices
                  </Text>
                </div>
                <Switch
                  checked={settings.push_notifications}
                  onChange={(checked) => handleSettingChange('push_notifications', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>SMS Notifications</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Receive SMS updates for important events
                  </Text>
                </div>
                <Switch
                  checked={settings.sms_notifications}
                  onChange={(checked) => handleSettingChange('sms_notifications', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Order Notifications</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Get notified when new orders are placed
                  </Text>
                </div>
                <Switch
                  checked={settings.order_notifications}
                  onChange={(checked) => handleSettingChange('order_notifications', checked)}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <>
                <BellOutlined /> Marketing Preferences
              </>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Promotional Emails</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Receive emails about promotions and features
                  </Text>
                </div>
                <Switch
                  checked={settings.promotional_emails}
                  onChange={(checked) => handleSettingChange('promotional_emails', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Newsletter</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Subscribe to our monthly newsletter
                  </Text>
                </div>
                <Switch
                  checked={settings.newsletter}
                  onChange={(checked) => handleSettingChange('newsletter', checked)}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <>
                <LockOutlined /> Privacy Settings
              </>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Profile Visibility</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Make your profile visible to customers
                  </Text>
                </div>
                <Switch
                  checked={settings.profile_visibility}
                  onChange={(checked) => handleSettingChange('profile_visibility', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Show Contact Info</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Display phone number and email to customers
                  </Text>
                </div>
                <Switch
                  checked={settings.show_contact_info}
                  onChange={(checked) => handleSettingChange('show_contact_info', checked)}
                />
              </div>

              <Divider />

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <Text strong>Online Status</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Show when you're available for orders
                  </Text>
                </div>
                <Switch
                  checked={settings.is_online}
                  onChange={(checked) => handleSettingChange('is_online', checked)}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Account Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Change Password</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Update your password to keep your account secure
                </Text>
                <br />
                <Button
                  type="default"
                  style={{ marginTop: 8 }}
                  onClick={() => setPasswordModalVisible(true)}
                >
                  Change Password
                </Button>
              </div>

              <Divider />

              <div>
                <Text strong>Two-Factor Authentication</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Add an extra layer of security to your account
                </Text>
                <br />
                <Button
                  type="default"
                  style={{ marginTop: 8 }}
                  onClick={() => handleToggle2FA(!settings.two_factor_enabled)}
                >
                  {settings.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                </Button>
              </div>

              <Divider />

              <div>
                <Text strong style={{ color: '#ff4d4f' }}>
                  Delete Account
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Permanently delete your account and all data
                </Text>
                <br />
                <Button danger style={{ marginTop: 8 }}>
                  Delete Account
                </Button>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Button
          type="primary"
          size="large"
          loading={loading || updateSettingsMutation.isLoading}
          icon={<SaveOutlined />}
          onClick={handleSave}
        >
          Save All Settings
        </Button>
      </div>

      <Modal
        title="Change Password"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            name="current_password"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password placeholder="Enter current password" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            name="new_password_confirmation"
            label="Confirm New Password"
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={changePasswordMutation.isLoading}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Enable Two-Factor Authentication"
        open={twoFactorModalVisible}
        onCancel={() => {
          setTwoFactorModalVisible(false);
          setTwoFactorData(null);
          form.resetFields();
        }}
        footer={null}
        width={400}
      >
        {twoFactorData ? (
          <>
            <Alert
              message="Scan QR Code"
              description="Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <img src={twoFactorData.qr_code} alt="QR Code" style={{ width: 200, height: 200 }} />
            </div>
            <Text
              type="secondary"
              style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}
            >
              Or enter this secret manually: <Text code>{twoFactorData.secret}</Text>
            </Text>
            <Form form={form} layout="vertical" onFinish={handleSetup2FA}>
              <Form.Item
                name="code"
                label="Authentication Code"
                rules={[{ required: true, message: 'Please enter the 6-digit code' }]}
              >
                <Input placeholder="Enter 6-digit code" maxLength={6} />
              </Form.Item>
              <Form.Item
                name="password"
                label="Your Password"
                rules={[{ required: true, message: 'Please enter your password' }]}
              >
                <Input.Password placeholder="Enter your password to confirm" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" block loading={twoFactorLoading}>
                  Enable 2FA
                </Button>
              </Form.Item>
            </Form>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        )}
      </Modal>

      <Modal
        title="Disable Two-Factor Authentication"
        open={disable2FAModalVisible}
        onCancel={() => {
          setDisable2FAModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Alert
          message="Disable 2FA"
          description="Enter your password to disable Two-Factor Authentication. Your account will be less secure."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical" onFinish={handleDisable2FA}>
          <Form.Item
            name="password"
            label="Your Password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password placeholder="Enter your password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" danger htmlType="submit" block loading={twoFactorLoading}>
              Disable 2FA
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
